import React, { useState, useRef, useEffect, useCallback } from 'react';
import { io } from 'socket.io-client';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import SEO from '../components/SEO';
import { Send, Loader2, Plus, Trash2, MessageSquare, PanelLeftClose, PanelLeft } from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api } from '../services/api';

const OPENING_MESSAGE = {
  role: 'assistant',
  content: "What's on your mind? You can ask anything about life, meaning, suffering, love, loss, purpose, or anything else. No judgment. No dogma. Just honesty.",
};

const SAMPLE_QUESTIONS = [
  "What are the Seven Patterns that all religions share?",
  "How do I do the Three-Minute Reset?",
  "I have everything but feel nothing. Why?",
  "What happens when we die?",
  "Why do good people suffer?",
  "How does quantum physics connect to spirituality?",
  "What is the ego, and how do I quiet it?",
  "I left religion but still feel spiritual. Is that okay?",
  "What did the Buddha, Jesus, and Rumi have in common?",
  "How do I find purpose when AI can do everything?",
  "What is karma, really?",
  "How do I let go without giving up?",
];

function getSocketUrl() {
  if (process.env.NODE_ENV === 'development') {
    return 'http://localhost:5019';
  }
  return window.location.origin;
}

export default function Ask() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [messages, setMessages] = useState([OPENING_MESSAGE]);
  const initialQ = searchParams.get('q') || '';
  const [input, setInput] = useState(initialQ);
  const [isStreaming, setIsStreaming] = useState(false);
  const [connected, setConnected] = useState(false);
  const [usageInfo, setUsageInfo] = useState(null);
  const [limitReached, setLimitReached] = useState(false);
  const socketRef = useRef(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Premium sidebar state
  const [chatList, setChatList] = useState([]);
  const [activeChatId, setActiveChatId] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [loadingChats, setLoadingChats] = useState(false);

  const tier = usageInfo?.tier || user?.tier || 'free';
  const isPremium = tier === 'premium';

  // Clear ?q= param after consuming it
  useEffect(() => {
    if (initialQ) {
      setSearchParams({}, { replace: true });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const scrollToBottom = useCallback((smooth = true) => {
    const el = messagesEndRef.current;
    if (!el) return;
    const container = el.parentElement?.parentElement;
    if (container) {
      const nearBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 150;
      if (nearBottom) {
        el.scrollIntoView({ behavior: smooth ? 'smooth' : 'instant' });
      }
    }
  }, []);

  const lastMsgCount = useRef(messages.length);
  useEffect(() => {
    if (messages.length > lastMsgCount.current) {
      scrollToBottom(false);
    }
    lastMsgCount.current = messages.length;
  }, [messages.length, scrollToBottom]);

  useEffect(() => {
    if (!isStreaming) return;
    const interval = setInterval(() => scrollToBottom(false), 300);
    return () => clearInterval(interval);
  }, [isStreaming, scrollToBottom]);

  // Load chat list for premium users
  useEffect(() => {
    if (!isPremium) return;
    setLoadingChats(true);
    api.getChats()
      .then(data => setChatList(data.chats || []))
      .catch(() => {})
      .finally(() => setLoadingChats(false));
  }, [isPremium]);

  // Socket connection
  useEffect(() => {
    const token = localStorage.getItem('auth_token');
    const socket = io(getSocketUrl(), {
      transports: ['polling', 'websocket'],
      path: '/socket.io',
      auth: token ? { token } : {},
    });

    socket.on('connect', () => {
      setConnected(true);
      socket.emit('chat_usage_check');
    });
    socket.on('disconnect', () => setConnected(false));

    socket.on('chat_chunk', (data) => {
      setMessages(prev => {
        const updated = [...prev];
        const last = updated[updated.length - 1];
        if (last && last.role === 'assistant' && last.streaming) {
          updated[updated.length - 1] = { ...last, content: last.content + data.content };
        }
        return updated;
      });
    });

    socket.on('chat_done', (data) => {
      setMessages(prev => {
        const updated = [...prev];
        const last = updated[updated.length - 1];
        if (last && last.role === 'assistant' && last.streaming) {
          updated[updated.length - 1] = { ...last, streaming: false };
        }
        return updated;
      });
      setIsStreaming(false);
      if (data.daily_remaining !== undefined && data.daily_remaining !== null) {
        setUsageInfo(prev => ({ ...prev, remaining: data.daily_remaining }));
        if (data.daily_remaining <= 0) setLimitReached(true);
      }
    });

    socket.on('chat_error', (data) => {
      setMessages(prev => [
        ...prev,
        { role: 'assistant', content: data.message || 'Something went wrong. Please try again.' },
      ]);
      setIsStreaming(false);
    });

    socket.on('chat_limit_reached', () => {
      setLimitReached(true);
      setUsageInfo(prev => ({ ...prev, remaining: 0 }));
    });

    socket.on('chat_usage_status', (data) => {
      setUsageInfo(data);
      if (data.remaining === 0 && data.tier !== 'premium') setLimitReached(true);
    });

    // Premium: chat loaded from server
    socket.on('chat_loaded', (data) => {
      const msgs = (data.messages || []).map(m => ({ role: m.role, content: m.content }));
      setMessages(msgs.length > 0 ? msgs : [OPENING_MESSAGE]);
    });

    // Premium: chat auto-renamed
    socket.on('chat_renamed', (data) => {
      setChatList(prev => prev.map(c =>
        c._id === data.chatId ? { ...c, title: data.title } : c
      ));
    });

    socketRef.current = socket;

    return () => { socket.disconnect(); };
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Premium: create new chat
  const handleNewChat = async () => {
    try {
      const { chat } = await api.createChat();
      setChatList(prev => [chat, ...prev]);
      setActiveChatId(chat._id);
      setMessages([OPENING_MESSAGE]);
      setLimitReached(false);
      if (socketRef.current) {
        socketRef.current.emit('chat_set_active', { chatId: chat._id });
      }
    } catch (err) {
      console.error('Failed to create chat:', err);
    }
  };

  // Premium: switch to a chat
  const handleSelectChat = (chatId) => {
    if (chatId === activeChatId || isStreaming) return;
    setActiveChatId(chatId);
    setMessages([{ role: 'assistant', content: 'Loading...', streaming: true }]);
    if (socketRef.current) {
      socketRef.current.emit('chat_load', { chatId });
    }
  };

  // Premium: delete a chat
  const handleDeleteChat = async (e, chatId) => {
    e.stopPropagation();
    try {
      await api.deleteChat(chatId);
      setChatList(prev => prev.filter(c => c._id !== chatId));
      if (activeChatId === chatId) {
        setActiveChatId(null);
        setMessages([OPENING_MESSAGE]);
        if (socketRef.current) {
          socketRef.current.emit('chat_reset');
        }
      }
    } catch (err) {
      console.error('Failed to delete chat:', err);
    }
  };

  const handleSend = async () => {
    const text = input.trim();
    if (!text || isStreaming || !socketRef.current || limitReached) return;

    // Premium: auto-create chat if none active
    if (isPremium && !activeChatId) {
      try {
        const { chat } = await api.createChat();
        setChatList(prev => [chat, ...prev]);
        setActiveChatId(chat._id);
        socketRef.current.emit('chat_set_active', { chatId: chat._id });
      } catch (err) {
        console.error('Failed to create chat:', err);
      }
    }

    const userMsg = { role: 'user', content: text };
    const assistantPlaceholder = { role: 'assistant', content: '', streaming: true };

    setMessages(prev => {
      // Remove opening message if it's the only one
      const filtered = prev.length === 1 && prev[0] === OPENING_MESSAGE ? [] : prev;
      return [...filtered, userMsg, assistantPlaceholder];
    });
    setInput('');
    setIsStreaming(true);

    socketRef.current.emit('chat_message', { message: text });
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Sidebar component for premium
  const Sidebar = () => (
    <div className={`${sidebarOpen ? 'w-56' : 'w-0'} flex-shrink-0 transition-all duration-200 overflow-hidden border-r border-dark-200/30 bg-dark-100/30 flex flex-col`}>
      <div className="p-3 border-b border-dark-200/30 flex items-center justify-between flex-shrink-0">
        <button
          onClick={handleNewChat}
          className="flex items-center gap-2 px-3 py-2 rounded-lg text-xs text-warm-100/60 hover:text-warm hover:bg-dark-200/30 transition-all flex-1"
        >
          <Plus size={14} />
          New Chat
        </button>
      </div>
      <div className="flex-1 overflow-y-auto py-2">
        {loadingChats ? (
          <p className="text-warm-100/30 text-xs px-3 py-2">Loading...</p>
        ) : chatList.length === 0 ? (
          <p className="text-warm-100/30 text-xs px-3 py-2">No conversations yet</p>
        ) : (
          chatList.map(chat => (
            <div
              key={chat._id}
              onClick={() => handleSelectChat(chat._id)}
              className={`group flex items-center gap-2 px-3 py-2.5 mx-1 rounded-lg cursor-pointer transition-all ${
                activeChatId === chat._id
                  ? 'bg-gold/10 text-warm'
                  : 'text-warm-100/50 hover:bg-dark-200/30 hover:text-warm-100/70'
              }`}
            >
              <MessageSquare size={13} className="flex-shrink-0 opacity-40" />
              <span className="text-xs truncate flex-1">{chat.title}</span>
              <button
                onClick={(e) => handleDeleteChat(e, chat._id)}
                className="opacity-0 group-hover:opacity-50 hover:!opacity-100 transition-opacity"
                title="Delete chat"
              >
                <Trash2 size={12} />
              </button>
            </div>
          ))
        )}
      </div>
    </div>
  );

  return (
    <div className="h-screen pt-16">
      <SEO path="/ask" />
      <div className="max-w-5xl mx-auto h-full flex px-6">
        {/* Premium sidebar */}
        {isPremium && <Sidebar />}

        {/* Chat column */}
        <div className="flex-1 flex flex-col min-w-0">
        {/* Header */}
        <div className="border-b border-dark-200/30 px-6 py-4 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {isPremium && (
                <button
                  onClick={() => setSidebarOpen(p => !p)}
                  className="text-warm-100/40 hover:text-warm transition-colors"
                  title={sidebarOpen ? 'Close sidebar' : 'Open sidebar'}
                >
                  {sidebarOpen ? <PanelLeftClose size={18} /> : <PanelLeft size={18} />}
                </button>
              )}
              <div>
                <h1 className="font-serif text-lg text-warm">AI Life Guide</h1>
                <p className="text-warm-100/40 text-xs mt-0.5">
                  Trained on the wisdom of all traditions
                </p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              {usageInfo && !isPremium && (
                <span className="text-warm-100/40 text-xs">
                  {usageInfo.remaining}/{usageInfo.limit} questions today
                </span>
              )}
              {isPremium && (
                <span className="bg-gold/15 text-gold text-[10px] px-2 py-0.5 rounded-full">Unlimited</span>
              )}
              <div className="flex items-center gap-2">
                <div className={`w-1.5 h-1.5 rounded-full ${connected ? 'bg-green-500/70' : 'bg-red-500/50'}`} />
                <span className="text-warm-100/30 text-xs">{connected ? 'Connected' : 'Connecting...'}</span>
              </div>
            </div>
          </div>
        </div>
          {/* Messages */}
          <div className="flex-1 overflow-y-auto px-6 py-6">
            <div className="max-w-3xl mx-auto space-y-6">
              {/* Sample questions */}
              {messages.length === 1 && messages[0] === OPENING_MESSAGE && (
                <div className="mt-4">
                  <p className="text-warm-100/40 text-xs uppercase tracking-wider mb-4 text-center">Try asking</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {SAMPLE_QUESTIONS.map((q, i) => (
                      <button
                        key={i}
                        onClick={() => {
                          setInput(q);
                          inputRef.current?.focus();
                        }}
                        className="text-left px-4 py-3 rounded-lg border border-dark-200/30 text-warm-100/60 text-sm hover:border-gold/30 hover:text-warm hover:bg-gold/5 transition-all"
                      >
                        {q}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {messages.map((msg, i) => (
                <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div
                    className={`max-w-[85%] md:max-w-[75%] ${
                      msg.role === 'user'
                        ? 'bg-gold/10 border border-gold/20 text-warm px-4 py-3 rounded-2xl rounded-br-sm'
                        : 'text-warm-100/80'
                    }`}
                  >
                    {msg.role === 'assistant' ? (
                      <div className="markdown leading-relaxed text-sm">
                        {msg.content ? (
                          <ReactMarkdown remarkPlugins={[remarkGfm, remarkBreaks]}>
                            {msg.content}
                          </ReactMarkdown>
                        ) : msg.streaming ? (
                          <div className="flex items-center gap-1.5 py-1">
                            <div className="w-1.5 h-1.5 bg-gold/50 rounded-full animate-pulse" />
                            <div className="w-1.5 h-1.5 bg-gold/50 rounded-full animate-pulse" style={{ animationDelay: '0.2s' }} />
                            <div className="w-1.5 h-1.5 bg-gold/50 rounded-full animate-pulse" style={{ animationDelay: '0.4s' }} />
                          </div>
                        ) : null}
                      </div>
                    ) : (
                      <p className="leading-relaxed text-sm">{msg.content}</p>
                    )}
                  </div>
                </div>
              ))}
              <div ref={messagesEndRef} />
            </div>
          </div>

          {/* Limit reached banner */}
          {limitReached && !isPremium && (
            <div className="border-t border-gold/20 bg-gold/5 px-6 py-4">
              <div className="max-w-3xl mx-auto text-center">
                <p className="text-warm text-sm mb-2">You've reached your daily free limit</p>
                <p className="text-warm-100/50 text-xs mb-3">Upgrade to Premium for unlimited questions</p>
                <Link
                  to="/pricing"
                  className="inline-block bg-gold/15 border border-gold/30 text-gold px-6 py-2 rounded-lg text-sm hover:bg-gold/25 transition-colors"
                >
                  View Plans
                </Link>
              </div>
            </div>
          )}

          {/* Input */}
          <div className="border-t border-dark-200/30 px-6 py-4 bg-dark/80 backdrop-blur-sm">
            <div className="max-w-3xl mx-auto flex items-end gap-3">
              <textarea
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={limitReached && !isPremium ? 'Daily limit reached - upgrade to continue' : 'Ask anything about life, meaning, purpose...'}
                rows={1}
                disabled={limitReached && !isPremium}
                className="flex-1 bg-dark-100 border border-dark-200/50 rounded-lg px-4 py-3 text-warm text-sm resize-none focus:outline-none focus:border-gold/30 placeholder-warm-100/30 font-serif disabled:opacity-40"
                style={{ minHeight: '44px', maxHeight: '120px' }}
                onInput={(e) => {
                  e.target.style.height = '44px';
                  e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px';
                }}
              />
              <button
                onClick={handleSend}
                disabled={!input.trim() || isStreaming || (limitReached && !isPremium)}
                className="p-3 bg-gold/10 border border-gold/30 text-gold rounded-lg hover:bg-gold/20 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
              >
                {isStreaming ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
